import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignaturePad from '@/components/ui/SignaturePad';
import StatusBadge from '@/components/ui/StatusBadge';
import { ApiError } from '@/lib/api';
import { contractPdfUrl, onboardingApi, type Contract, type ContractTemplate, type PartnerFull } from '@/lib/onboarding';
import { Field, Section } from './fields';
import { btnGhost, btnPrimary, inputCls } from './styles';

interface Props {
  partner: PartnerFull;
  onPartner: (p: PartnerFull) => void;
  onToast: (message: string, tone?: 'success' | 'error') => void;
  canEdit: boolean;
}

const toneOf = (s: string) => (s === 'Signed' ? 'primary' : s === 'Expired' ? 'danger' : 'accent');
const plusYear = (d: string) => { const x = new Date(d); x.setFullYear(x.getFullYear() + 1); x.setDate(x.getDate() - 1); return x.toISOString().slice(0, 10); };

/** Шаг 5: договор по шаблону Treejar с условиями партнёра, PDF, отправка, подпись на планшете или скан. */
export default function ContractStep({ partner, onPartner, onToast, canEdit }: Props) {
  const { t, i18n } = useTranslation();
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [current, setCurrent] = useState<Contract | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [terms, setTerms] = useState<Record<string, string>>({});
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(plusYear(today));
  const [signer, setSigner] = useState(partner.contact ?? '');
  const [signature, setSignature] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ru = i18n.language === 'ru';

  const load = useCallback(async () => {
    const [tp, cs] = await Promise.all([onboardingApi.templates(), onboardingApi.contracts(partner.id)]);
    setTemplates(tp.filter((x) => x.active));
    setContracts(cs);
    if (!templateId && tp.length) setTemplateId(tp[0].id);
    if (cs.length) setCurrent(await onboardingApi.contract(cs[0].id));
    else setCurrent(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner.id]);
  useEffect(() => {
    void load();
  }, [load]);

  const tpl = templates.find((x) => x.id === templateId);
  useEffect(() => {
    if (!tpl) return;
    const d: Record<string, string> = {};
    tpl.variables.forEach((v) => {
      const fromPartner: Record<string, unknown> = { reservation_respond_hours: partner.reservationRespondHours, reservation_days: partner.reservationPeriodDays, po_confirm_hours: partner.poConfirmHours, payment_lag_days: partner.paymentLagDays, warranty_months: partner.warrantyMonths };
      const val = fromPartner[v.key] ?? v.default;
      d[v.key] = val == null ? '' : String(val);
    });
    setTerms(d);
  }, [tpl, partner]);

  const create = async () => {
    if (!tpl) return;
    setBusy(true);
    try {
      const body: Record<string, unknown> = {};
      Object.entries(terms).forEach(([k, v]) => { body[k] = v !== '' && !Number.isNaN(Number(v)) ? Number(v) : v; });
      const c = await onboardingApi.createContract(partner.id, { templateId: tpl.id, terms: body, startDate: start, endDate: end });
      onToast(t('onb.ctrCreated', { number: c.number }));
      await load();
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    } finally {
      setBusy(false);
    }
  };
  const send = async () => {
    if (!current) return;
    try {
      await onboardingApi.sendContract(current.id);
      onToast(t('onb.ctrSent', { email: partner.email || '—' }));
      await load();
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    }
  };
  const sign = async () => {
    if (!current || !signature || !signer.trim()) return;
    setBusy(true);
    try {
      await onboardingApi.signContract(current.id, { method: 'tablet_signature', signerName: signer.trim(), signature });
      onToast(t('onb.ctrSignDone'));
      onPartner(await onboardingApi.partner(partner.id));
      await load();
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    } finally {
      setBusy(false);
    }
  };
  const scan = async (file: File | undefined) => {
    if (!current || !file || !signer.trim()) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('signerName', signer.trim());
    try {
      await onboardingApi.uploadScan(current.id, fd);
      onToast(t('onb.ctrSignDone'));
      onPartner(await onboardingApi.partner(partner.id));
      await load();
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    }
  };

  const signed = current?.status === 'Signed';
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <div className="flex flex-col gap-4 xl:col-span-2">
        <Section title={t('onb.ctrTitle')} action={current && <StatusBadge tone={toneOf(current.status)}>{t(`onb.ctrStatus.${current.status}`)}</StatusBadge>}>
          <p className="mb-4 text-[12.5px] leading-5 text-foreground-600">{t('onb.ctrDesc')}</p>
          {current ? (
            <div>
              <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px] text-foreground-600">
                <span className="font-semibold text-foreground-900">{current.number}</span>
                <span>v{current.version}</span>
                <span>{current.startDate} – {current.endDate}</span>
                {current.signedByName && <span>· {t('onb.ctrSigned', { name: current.signedByName })}</span>}
                <a href={contractPdfUrl(current.id, true)} target="_blank" rel="noreferrer" className={btnGhost}><i className="ri-file-pdf-2-line text-[13px]" /> {t('onb.ctrPdf')}</a>
                {canEdit && !signed && <button type="button" onClick={send} className={btnGhost}><i className="ri-mail-send-line text-[13px]" /> {t('onb.ctrSend')}</button>}
              </div>
              <div className="max-h-[520px] overflow-y-auto rounded-md border border-background-200 bg-background-100 p-4">
                <div className="mx-auto max-w-[680px] bg-background-50 px-8 py-8 text-[12.5px] leading-6 text-foreground-800 shadow-sm [&_h1]:mb-3 [&_h1]:text-[16px] [&_h1]:font-bold [&_h2]:mb-1 [&_h2]:mt-4 [&_h2]:text-[13px] [&_h2]:font-semibold [&_p]:my-1">
                  <div dangerouslySetInnerHTML={{ __html: current.html || '' }} />
                </div>
              </div>
            </div>
          ) : (
            <p className="text-[12.5px] text-foreground-500">{t('onb.ctrNone')}</p>
          )}
        </Section>
        {contracts.length > 1 && (
          <Section title={t('onb.ctrHistory')}>
            <ul className="divide-y divide-background-200 text-[12px]">
              {contracts.map((c) => (
                <li key={c.id} className="flex items-center justify-between py-2">
                  <span>{c.number} · v{c.version} · {c.startDate} – {c.endDate}</span>
                  <span className="flex items-center gap-2"><StatusBadge tone={toneOf(c.status)} dot={false}>{t(`onb.ctrStatus.${c.status}`)}</StatusBadge><a href={contractPdfUrl(c.id, true)} target="_blank" rel="noreferrer" className="text-primary-700">PDF</a></span>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
      <div className="flex flex-col gap-4">
        {canEdit && (!current || signed || current.status === 'Expired') && (
          <Section title={t('onb.ctrCreate')}>
            {templates.length === 0 ? (
              <p className="text-[12px] text-foreground-500">{t('onb.ctrNoTemplates')}</p>
            ) : (
              <div className="flex flex-col gap-3">
                <Field label={t('onb.ctrTemplate')}>
                  <select className={inputCls} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                    {templates.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.lang.toUpperCase()})</option>)}
                  </select>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t('onb.ctrStart')}><input className={inputCls} type="date" value={start} onChange={(e) => { setStart(e.target.value); setEnd(plusYear(e.target.value)); }} /></Field>
                  <Field label={t('onb.ctrEnd')}><input className={inputCls} type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
                </div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-foreground-500">{t('onb.ctrTerms')}</p>
                {tpl?.variables.map((v) => (
                  <Field key={v.key} label={ru ? v.label_ru : v.label_en}><input className={inputCls} value={terms[v.key] ?? ''} onChange={(e) => setTerms((x) => ({ ...x, [v.key]: e.target.value }))} /></Field>
                ))}
                <button type="button" onClick={create} disabled={busy || !tpl} className={btnPrimary}><i className="ri-file-add-line text-[14px]" /> {busy ? t('onb.ctrCreating') : t('onb.ctrCreate')}</button>
              </div>
            )}
          </Section>
        )}
        {canEdit && current && !signed && current.status !== 'Expired' && (
          <Section title={t('onb.ctrSign')}>
            <div className="flex flex-col gap-3">
              <Field label={t('onb.ctrSignerName')}><input className={inputCls} value={signer} onChange={(e) => setSigner(e.target.value)} /></Field>
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-foreground-500">{t('onb.ctrSignTablet')}</p>
              <SignaturePad onChange={setSignature} clearLabel={t('onb.apprClear')} hint={t('onb.apprSign')} />
              <button type="button" onClick={sign} disabled={busy || !signature || !signer.trim()} className={btnPrimary}><i className="ri-quill-pen-line text-[14px]" /> {t('onb.ctrSign')}</button>
              <label className={`${btnGhost} justify-center`}>
                <i className="ri-upload-2-line text-[13px]" /> {t('onb.ctrSignScan')}
                <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => void scan(e.target.files?.[0])} />
              </label>
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}
